import { Checkbox } from "@dash/form";
import { Skeleton } from "@dash/skeleton";
import { RowCellContent, Table, TableBody, TableHeadColumn, TableRowProps } from "@dash/table";
import { CSSObject, useTheme } from "@emotion/react";
import React, { useMemo } from "react";

import {
  getBaselineValue,
  getChangedFields,
  getDirectionValue,
  isDeletedRecord,
  isFieldAddition,
  isFieldDeletion,
  isNewRecord,
  RemovedValue,
  renderCellValue,
} from "./diffValues";
import { ScrollableTableFrame } from "./ScrollableTableFrame";
import { DiffSyncDirection, DiffSyncRecord, ValueFormatter } from "./types";

export const DiffSyncTable = <T extends Record<string, unknown>>(props: {
  direction: DiffSyncDirection;
  diffs: DiffSyncRecord<T>[] | undefined;
  selectedDiffIds: readonly string[];
  onSelectionChange: (diffIds: string[]) => void;
  labels?: Partial<Record<keyof T & string, string>>;
  formatters?: Partial<Record<keyof T & string, ValueFormatter>>;
  alwaysVisibleColumns?: readonly (keyof T & string)[];
  isLoading?: boolean;
}) => {
  const theme = useTheme();
  const columns = useMemo(
    () => getVisibleColumns(props.diffs ?? [], props.direction, props.alwaysVisibleColumns ?? []),
    [props.alwaysVisibleColumns, props.diffs, props.direction],
  );
  const selectedDiffIdSet = useMemo(() => new Set(props.selectedDiffIds), [props.selectedDiffIds]);

  if (!props.diffs) return <Skeleton />;

  const diffIds = props.diffs.map((diff) => diff.diffId);
  const visibleSelectedDiffIds = props.selectedDiffIds.filter((id) => diffIds.includes(id));
  const isAllSelected = diffIds.length > 0 && visibleSelectedDiffIds.length === diffIds.length;
  const selectionCellCss: CSSObject = {
    width: "1px",
    textAlign: "center",
  };

  const allColumns: TableHeadColumn<string>[] = [
    {
      label: "",
      element: (
        <Checkbox
          value={isAllSelected}
          onChange={(isSelected) => props.onSelectionChange(isSelected ? diffIds : [])}
          isDisabled={diffIds.length === 0}
        />
      ),
      cellCss: selectionCellCss,
    },
    { label: "Id" },
    ...columns.map((key) => ({ label: props.labels?.[key] ?? key })),
  ];

  const newRecordCss: CSSObject = {
    background: theme.colors.primaryLight,
    color: theme.colors.primary,
    fontWeight: 700,
  };

  const modificationCss: CSSObject = {
    background: theme.colors.warningBackground,
    color: theme.colors.warning,
    fontWeight: 700,
  };

  const deletionCss: CSSObject = {
    background: theme.colors.dangerBackground,
    color: theme.colors.danger,
    fontWeight: 700,
  };

  const rows: TableRowProps[] = props.diffs.map((diff) => {
    const changedFields = getChangedFields(diff, props.direction);
    const isNew = isNewRecord(diff, props.direction);
    const isDeleted = isDeletedRecord(diff, props.direction);
    const source = getDirectionValue(diff, props.direction);
    const baseline = getBaselineValue(diff, props.direction);
    const isSelected = selectedDiffIdSet.has(diff.diffId);
    const toggleSelection = (selected: boolean) => {
      if (selected) {
        props.onSelectionChange(Array.from(new Set([...visibleSelectedDiffIds, diff.diffId])));
      } else {
        props.onSelectionChange(visibleSelectedDiffIds.filter((id) => id !== diff.diffId));
      }
    };

    return {
      id: diff.diffId,
      cellsContent: [
        {
          element: <Checkbox value={isSelected} onChange={toggleSelection} />,
          cellCss: selectionCellCss,
        },
        {
          element: isDeleted ? <RemovedValue value={diff.diffId} /> : diff.diffId,
          cellCss: isNew ? newRecordCss : isDeleted ? deletionCss : undefined,
        },
        ...columns.map<RowCellContent>((key) => {
          const isChanged = changedFields.has(key);
          const isAddition = isChanged && !isNew && isFieldAddition(source, baseline, key);
          const isDeletion = isChanged && !isNew && isFieldDeletion(source, baseline, key);
          let cellCss: CSSObject | undefined;

          if (isNew || isAddition) cellCss = newRecordCss;
          else if (isDeletion || isDeleted) cellCss = deletionCss;
          else if (isChanged) cellCss = modificationCss;

          return {
            element: renderCellValue(source, baseline, key, isChanged, isNew, props.formatters?.[key]),
            cellCss,
          };
        }),
      ],
    };
  });

  return (
    <ScrollableTableFrame>
      <Table
        columns={allColumns}
        css={{
          whiteSpace: "nowrap",
        }}
      >
        <TableBody isLoading={props.isLoading} rows={rows} />
      </Table>
      {rows.length === 0 && (
        <div
          css={{
            padding: "1rem",
            color: theme.colors.primary,
            opacity: 0.75,
          }}
        >
          Bez zmian
        </div>
      )}
    </ScrollableTableFrame>
  );
};

const getVisibleColumns = <T extends Record<string, unknown>>(
  diffs: DiffSyncRecord<T>[],
  direction: DiffSyncDirection,
  alwaysVisibleColumns: readonly (keyof T & string)[],
) =>
  Array.from(
    new Set([...alwaysVisibleColumns, ...diffs.flatMap((diff) => Array.from(getChangedFields(diff, direction)))]),
  );
